import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, shallowEqual } from "react-redux";
import AskValue from "../global/BidAsk/AskValue";
import BidValue from "../global/BidAsk/BidValue";
import ProfitLoss from "../global/BidAsk/ProfitLoss";
import moment from "moment";
import { Tooltip } from "@mui/material";
import { MdOutlineEdit } from "react-icons/md";
import i18n from "i18next";
import Commission from "../global/BidAsk/Commission";

const defaultRender = (value) => (value ? value : "--");

// const formatDateTime = (dateTime) => moment.utc(dateTime).local().format('DD-MM-YYYY HH:mm:ss');

const formatPrice = (value, decimalPlaces) => {
  if (value === null || value === undefined) return "";
  return Number(value)?.toFixed(decimalPlaces);
};

const offsetToMinutes = (offset) => {
  const [hours, minutes, seconds] = offset.split(":").map(Number);
  return hours * 60 + minutes + Math.sign(hours) * (seconds > 0 ? 1 : 0);
};


const useTradeCell = (onEditSLTP) => {
  const { t } = useTranslation();
  const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
  const orderSLTP = useSelector((state) => state.orderPosition.orderSLTP, shallowEqual);
  const profitLossData = useSelector((state) => state.profitLoss.profitLossData);
  const { symbols } = useSelector(state => state.watchlist)
  const { UserCompleteDetails } = useSelector(state => state.user);

const formatDateTime = (serverDateTime) => {
  if (!serverDateTime) return "--";

  const serverTime = moment.parseZone(serverDateTime);
  const serverOffsetMin = offsetToMinutes(UserCompleteDetails?.serverGMTOffset || "00:00:00");
  const clientOffsetMin = offsetToMinutes(UserCompleteDetails?.gmtOffset || "00:00:00");
  const diffOffset = clientOffsetMin - serverOffsetMin;

  return serverTime.add(diffOffset, "minutes").format("DD-MM-YYYY HH:mm:ss");
};

  const getDigitsBySymbolId = (symbolId) => {
    return symbols?.[symbolId]?.digits;
  };
  const columns = useMemo(() => ([
    { title: t("ticket"), width: 70, dataIndex: "id", render: defaultRender },
    { title: t("date/time"), width: 140, dataIndex: "dateTime", render: (value) => formatDateTime(value) },
    { title: t("type"), width: 60, dataIndex: "side", render: (value, row) => row.side === 1 ? <span className="green">{t('buy')}</span> : <span className="red">{t('sell')}</span> },
    { title: t("amount"), width: 100, dataIndex: "amount", render: (value) => formatPrice(value, decimalPlaces) },

    { title: t("symbol"), width: 100, dataIndex: "symbolName", render: defaultRender },
    {
      title: t("openPrice"), width: 120, dataIndex: "openPrice", render: (value, row) => {
        const digits = getDigitsBySymbolId(row.symbolId);
        return value?.toFixed?.(digits);
      }
    },
    {
      title: t("currentPrice"), width: 140, dataIndex: "currentPrice",
      render: (value, row) =>
        row?.side === -1 ? <AskValue symbolId={row.symbolId} digits={row.digits} /> : <BidValue symbolId={row.symbolId} digits={row.digits} />
    },
    {
      title: t("s/l"), width: 80, dataIndex: "stopLoss",
      render: (value, row) => {
        const sltpList = orderSLTP.filter((sltp) => sltp.positionId === row.id && sltp.stopLoss !== undefined && sltp.stopLoss !== null);
        const digits = getDigitsBySymbolId(row.symbolId);

        if (sltpList.length === 0) return "--";

        const allSLs = sltpList.map(sltp => sltp.stopLoss).filter(Boolean);
        const displaySL = allSLs[0]?.toFixed?.(digits) + (allSLs.length > 1 ? ", ..." : "");

        return (
          <Tooltip title={allSLs.join(", ")}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {displaySL ? displaySL : ''}
              <MdOutlineEdit fontSize="small" style={{ cursor: "pointer" }} onClick={() => onEditSLTP(row)} />
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: t("t/p"), width: 80, dataIndex: "takeProfit",
      render: (value, row) => {
        const sltpList = orderSLTP.filter((sltp) => sltp.positionId === row.id && sltp.takeProfit !== undefined && sltp.takeProfit !== null);
        if (sltpList.length === 0) return "--";
        const digits = getDigitsBySymbolId(row.symbolId);

        const allTPs = sltpList.map(sltp => sltp.takeProfit);
        const displayTP = allTPs[0]?.toFixed?.(digits) + (allTPs.length > 1 ? ", ..." : "");

        return (
          <Tooltip title={allTPs.join(", ")}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {displayTP}
              <MdOutlineEdit fontSize="small" style={{ cursor: "pointer" }} onClick={() => onEditSLTP(row)} />
            </span>
          </Tooltip>
        );
      }
    }
    ,


    { title: t("commission"), width: 100, dataIndex: "commission", render: (value, row) => <Commission row={row} /> },
    { title: t("interest"), width: 90, dataIndex: "interest", render: (value) => value?.toFixed?.(2) ?? "-" },

    {
      title: t("P/L"), width: 100, dataIndex: "profit",
      render: (value, row) => <ProfitLoss row={{ ...row, profit: profitLossData[row.id] ?? value }} />
    },
    { title: t("comment"), width: 100, dataIndex: "comment", render: defaultRender },
  ]), [orderSLTP, decimalPlaces, i18n.language]);


  return columns;
};

export default useTradeCell;
